import {useQueryClient} from '@tanstack/react-query';
import {Button, Card, Popconfirm, Spin, Tag, message} from 'antd';
import {format} from 'date-fns';
import {jwtDecode} from 'jwt-decode';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';

import {RQGetOrderByUserId, RqUpdateOrder} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

const OrderHistory = () => {
  const auth = useSelector(selectAuth);

  const queryClient = useQueryClient();

  const token = auth?.tokenData;

  const decodedToken = token ? jwtDecode(token) : null;

  const userId = decodedToken?.userId;

  const [isUpdating, setIsUpdating] = useState(false);

  const {data: orders, isLoading} = RQGetOrderByUserId(userId, token);

  const {mutate: updateOrder} = RqUpdateOrder();

  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center py-10'}>
        <Spin />
      </div>
    );
  }

  const getStatusColor = status => {
    switch (status) {
      case 'PENDING':
        return 'orange';
      case 'CONFIRMED':
        return 'green';
      case 'SHIPPED':
        return 'blue';
      case 'DELIVERED':
        return 'purple';
      case 'CANCELLED':
        return 'red';
      default:
        return 'default';
    }
  };

  const canCancel = status =>
    status !== 'DELIVERED' &&
  status !== 'CANCELLED' &&
  status !== 'SHIPPED';

  const handleCancelOrder = orderId => {
    setIsUpdating(true);

    updateOrder(
      {
        id: orderId,
        token,
        formData: {status: 'CANCELLED'},
      },
      {
        onSuccess: () => {
          toast.success('Order cancelled successfully ');

          queryClient.invalidateQueries(['orders']);
        },
        onError: error => {
          console.error('Cancel order failed:', error);
          toast.error('Failed to cancel order ');
        },
        onSettled: () => {
          setIsUpdating(false);
        },
      },
    );
  };
  const toDropboxRawUrl = (url = '') =>
    url
      .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');

  return (
    <div className={'w-full flex flex-col gap-4'}>
      <h1 className={'header'}>Orders</h1>

      {!orders || orders.length === 0 ? (
        <p className={'text-center text-gray-500'}>
          You haven’t placed any orders yet.
        </p>
      ) : (
        [...orders]
          .sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
          .map((order, index) => (
            <Card key={order.id} className={'shadow-md'}>
              {/* Header */}
              <div
                className={
                  'flex md:flex-row flex-col md:justify-between md:items-center mb-3 gap-2'
                }
              >
                <div className={'flex flex-col'}>
                  <div className={'flex gap-1'}>
                    <p className={'font-semibold'}>Date:</p>
                    <p>{format(new Date(order.createdOn), 'dd/MM/yyyy')}</p>
                  </div>

                  <p className={'text-sm font-medium text-gray-600'}>
                    Payment:{' '}
                    {order.products?.[0]?.paymentMethod === 'COD'
                      ? 'Cash on Delivery'
                      : 'paid'}
                  </p>
                </div>

                <div className={'flex justify-end items-center gap-2'}>
                  <Tag color={getStatusColor(order.status)}>{order.status}</Tag>

                  <Popconfirm
                    title={'Are you sure you want to cancel this order?'}
                    okText={'Yes'}
                    cancelText={'No'}
                    onConfirm={() => handleCancelOrder(order.id)}
                    onCancel={() => toast('Cancelled action')}
                  >
                    <Button
                      danger
                      loading={isUpdating}
                      disabled={!canCancel(order.status)}
                    >
    Cancel order
                    </Button>
                  </Popconfirm>
                </div>
              </div>

              <div className={'flex flex-col gap-3 max-h-[300px] overflow-y-auto pr-1'}>
                {order.products.map(product => {
                  const variants = product.productInfo?.variants || [];
                  const matchedVariant = variants.find(
                    v => v.color === product.color,
                  );

                  const imageUrl =
                    matchedVariant?.images?.[0] ||
                    product.productInfo?.imageUrl?.[0] ||
                    '';

                  const pricePerUnit = Number(product.productInfo?.price) || 0;

                  const discountPercent =
                    Number(product.productInfo?.discount) || 0;

                  const discountedPrice =
                    pricePerUnit - (pricePerUnit * discountPercent) / 100;

                  return (
                    <div
                      key={product.productId}
                      className={'flex items-start md:items-center gap-4 border-b pb-3 border-primary-50 last:border-b-0'}
                    >
                      {/* Image */}
                      <Link
                        to={`/product/${product.productId}`}
                        className={'block flex-shrink-0'}
                      >
                        <img
                          src={toDropboxRawUrl(imageUrl)}
                          alt={product.productInfo?.productName}
                          className={'w-16 h-16 min-w-[64px] flex-shrink-0 object-cover rounded border border-primary-100 transition-transform duration-200 md:hover:scale-105'}
                          style={{imageRendering: 'auto'}}
                        />
                      </Link>
                      <div
                        className={
                          'flex flex-col md:flex-row w-full md:justify-between'
                        }
                      >
                        {/* Name */}
                        <div className={'md:flex-1'}>
                          <p className={'font-semibold'}>
                            {product.productInfo?.productName}
                          </p>
                        </div>

                        {/* Details */}
                        <div className={'mt-2 md:mt-0 overflow-x-auto'}>
                          <div className={'flex gap-6 min-w-max'}>

                            <div className={'text-sm'}>
                              <p className={'font-bold'}>Qty</p>
                              <p>{product.quantity}</p>
                            </div>

                            <div className={'text-sm text-center'}>
                              <p className={'font-bold'}>Size</p>
                              <p>{product.size || 'N/A'}</p>
                            </div>

                            <div className={'text-sm text-center'}>
                              <p className={'font-bold'}>Color</p>
                              <p>{product.color}</p>
                            </div>

                            <div className={'text-sm text-center'}>
                              <p className={'font-bold'}>MRP</p>
                              <p>₹ {discountedPrice}</p>
                            </div>

                            <div className={'text-sm text-center'}>
                              <p className={'font-bold'}>SubTotal</p>
                              <p>₹ {Math.round(discountedPrice * product.quantity)}</p>
                            </div>

                          </div>
                        </div>
                      </div>
                     
                    </div>
                  );
                })}
              </div>
              
              <div
                className={
                  'flex justify-end mt-2 pt-2 border-t border-primary-100'
                }
              >
                <p className={'text-base font-bold'}>
                  Total: ₹ {Math.round(order.totalAmount)}
                </p>
              </div>
            </Card>
          ))
      )}
    </div>
  );
};

export default OrderHistory;
